interface IConfigAuth {
  accessTokenUri: string;
  clientId: string;
  clientSecret: string;
}

interface IConfigLiveStatus {
  enabled: boolean;
  ignore_station_types: string[];
}

interface IConfigSip {
  host: string;
  user: string;
  password: string;
  wsServers: string;
}

interface IConfigConnectServer {
  url: string;
}

interface IConfigInmate {
  pinEnabled: boolean;
  personIdEnabled: boolean;
  bookingIdEnabled: boolean;
  itdsEnabled: boolean;
  housingUnitsEnabled: boolean;
  pinLength: number;
  addFundsDefaultAmountOptions: number[];
  purchaseItems?: IInmatePurchaseItem[];
}

interface IConfigVisitation {
  type: 'fs-local-video' | 'fs-audio';
  timeLimitOptions: number[];
  defaultTimeLimit: number;
}

interface IConfigSearch {
  defaultStartDatePeriod: number;
  outbound: IConfigSearchOutbound;
  inbound: IConfigSearchInbound;
  visitation: IConfigSearchVisitation;
}

interface IConfigSearchOutbound {
  defaultMinDuration: number;
  defaultMaxDuration?: number;
  startDatePeriod: number;
  criteriaChoices: string[];
  defaultCriteriaChoice: string;
  callTypes: { [key: string]: string };
  defaultCallTypes: string[];
}

interface IConfigSearchInbound {
  startDatePeriod: number;
  source: string;
}

interface IConfigSearchVisitation {
  startDatePeriod: number;
  defaultMinDuration: number;
  defaultMaxDuration?: number;
}

interface IConfigApi {
  url: string;
}

interface IConfigPrivateNumber {
  readOnly: boolean;
}

export type Feature =
  | 'audio-call'
  | 'audio-visitation'
  | 'commissary'
  | 'inmate-requests'
  | 'job-search'
  | 'law-library'
  | 'reference-material'
  | 'texting'
  | 'video-call'
  | 'visitation'
  | 'voicemail'
  | 'wireless-audio-calls'
  | 'inmate-communication-policy';

export interface IConfig {
  version: string;
  usage?: 'cloud' | 'site';
  siteOverrideUrl?: string; // Allows overriding the "hostname" lookup to use an alternative URL
  name: string;
  timezone: string;
  baseUrl: string;
  nodeServer: string;
  auth: IConfigAuth;
  liveStatus: IConfigLiveStatus;
  sip: IConfigSip;

  phpDateFormat: string;
  urlDateFormat: string;
  urlDateRegex: RegExp;
  connectServer?: IConfigConnectServer;
  inmate: IConfigInmate;
  visitation: IConfigVisitation;
  features?: Feature[];
  search: IConfigSearch;
  api: IConfigApi;
  centralApi: IConfigApi;
  privateNumber: IConfigPrivateNumber;
}

export interface IInmatePurchaseItem {
  cost: number;
  title: string;
  transactionDescription: string;
}

const baseConfig: IConfig =
  process.env.APP_USAGE && process.env.APP_USAGE == 'cloud'
    ? {
        usage: 'cloud',
        siteOverrideUrl: process.env.SITES_OVERRIDE_URL
          ? process.env.SITES_OVERRIDE_URL
          : null,
        nodeServer: process.env.NODE_SERVER ? process.env.NODE_SERVER : null,
        api: {
          url: process.env.API_URL
        },
        centralApi: {
          url: process.env.API_URL
        },
        auth: {
          clientId: 'relcore-site',
          accessTokenUri: process.env.API_URL + '/auth/token'
        },
        features: [],
        liveStatus: {
          enabled: false
        },
        phpDateFormat: 'YYYY-MM-DD HH:mm:ss',
        urlDateFormat: 'YYYYMMDD_HHmmss',
        urlDateRegex: /\d{8}_\d{6}/,
        inmate: {
          pinEnabled: true, // Enable inmate PINs
          personIdEnabled: false, // Enable person IDs
          bookingIdEnabled: false, // Enable booking IDs
          itdsEnabled: false, // Enable ITDS features
          housingUnitsEnabled: true,
          pinLength: 6,
          addFundsDefaultAmountOptions: [] // Default amounts to populate the add funds form with
        },
        privateNumber: {
          readOnly: false
        },
        visitation: {},
        search: {
          defaultStartDatePeriod: -1,
          outbound: {
            defaultMinDuration: 0,
            defaultMaxDuration: null,
            startDatePeriod: 1,

            // Options: Destination, PhoneCard, Inmate
            criteriaChoices: ['Destination', 'PhoneCard', 'Inmate'],

            // One of the above criteriaChoices options
            defaultCriteriaChoice: 'Destination',

            callTypes: {
              'audio-call': 'Audio',
              InfoCenterSetup: 'InfoCenter Setup',
              CheckVoicemail: 'Check Voicemail',
              CourtesyCall: 'Courtesy Call',
              PhoneCardBalance: 'Phonecard Balance',
              PhoneCardTransfer: 'Phonecard Transfer',
              SupportMessage: 'Support Message',
              Setup: 'Setup',
              Normal: 'Normal',
              PhoneCard: 'Phonecard',
              InternationalPhoneCard: 'International Phonecard',
              'video-call': 'Video'
            },

            defaultCallTypes: [
              'audio-call',
              'Normal',
              'PhoneCard',
              'InternationalPhoneCard',
              'VideoCall'
            ]
          },
          inbound: {
            startDatePeriod: 1,
            source: 'central'
          },
          visitation: {
            startDatePeriod: 1,
            defaultMinDuration: 60
          }
        }
      }
    : require('RwisConfig');

interface IConfigService extends IConfig {
  hasFeature(feature: Feature): boolean;
  hasOneOfFeatures(features: Feature[]): boolean;
}

const config: IConfigService = {
  ...baseConfig,
  hasFeature(feature: Feature) {
    return (this.features || []).indexOf(feature) > -1;
  },
  hasOneOfFeatures(features: Feature[]) {
    return features.reduce((prev, cur) => prev || this.hasFeature(cur), false);
  }
};

export default config;
